import { useId, type ReactNode } from 'react';

import { StatusMessage } from './StatusMessage';
import { safeId, wireControl, type ControlAttrs } from './labelledControl';

// ─────────────────────────────────────────────────────────────────────
// THE PRIMITIVE MEANT TO SOLVE LABELLING DID NOT LABEL ANYTHING (#781 slice 10).
//
// It rendered `<label>…</label>` and then `{children}` as a SIBLING, with no
// `htmlFor` and no id. Visually that is a labelled field; programmatically the
// label belongs to nothing, so clicking it does not focus the control and a
// screen reader reaches the input with no name at all. The app has 417 form
// controls and ~294 of them have no programmatic label — and this is the shared
// component those call sites were supposed to be fixed with.
//
// So the fix is here rather than at the call sites, and the call sites are
// deliberately NOT touched in this slice: there are 25 of them in four
// directories that other work is moving at the same time, and a `<Field>` whose
// child is a single element is wired with no edit at all (see `wireControl`).
// ─────────────────────────────────────────────────────────────────────

export interface FieldProps {
  /** Field label (mono, uppercase). */
  label: ReactNode;
  /**
   * The input/control the label describes.
   *
   * A single element child is wired automatically. Pass a FUNCTION to wire it
   * by hand — `{(control) => <MyThing {...control} />}` — which is the escape
   * hatch for a field whose control is nested, repeated, or built from
   * fragments.
   */
  children: ReactNode | ((control: ControlAttrs) => ReactNode);
  /** Tighter label sizing for dense layouts. */
  small?: boolean;
  /**
   * The control's id, when the caller owns it. Generated otherwise.
   *
   * Named after the DOM attribute it becomes, not after the control, because
   * that is what a reader of the call site is looking for.
   */
  htmlFor?: string;
  /**
   * The validation message. Rendered as an assertive live region and pointed at
   * by the control's `aria-describedby`; also implies `aria-invalid`.
   */
  error?: ReactNode;
  /** Help text, pointed at by the control's `aria-describedby`. */
  hint?: ReactNode;
  /**
   * Marks the control invalid without a message. Defaults to `!!error` — an
   * error that is shown is an error that happened.
   */
  invalid?: boolean;
  /**
   * The children are a SET of controls, not one — a row of choice buttons, a
   * pair of inputs, a read-only value. The field becomes a named
   * `role="group"` and the title is no longer a `<label>`.
   *
   * WHY A SEPARATE MODE: a `<label>` can only name one labelable element. Aimed
   * at a wrapper `<div>` it names nothing, and that is what every `Field` round
   * a button row did until #900: the title read correctly and was tied to
   * nothing. A group's name is announced as focus enters it, which is what the
   * title was for.
   */
  group?: boolean;
}

/**
 * Labelled form field — a mono, uppercase label above the control it names.
 *
 * ```tsx
 * <Field label={t('players.common.name')} error={nameError}>
 *   <input className="input" value={name} onChange={…} />
 * </Field>
 * ```
 *
 * Markup and classes are unchanged from the monolith extraction, so the look is
 * identical; what is new is that the label, the control, the hint and the error
 * are connected to each other.
 */
export function Field({
  label,
  children,
  small,
  htmlFor,
  error,
  hint,
  invalid,
  group,
}: FieldProps) {
  const auto = useId();
  const control: ControlAttrs = {
    id: htmlFor ?? safeId('field', auto),
    'aria-describedby':
      [hint ? safeId('hint', auto) : null, error ? safeId('error', auto) : null]
        .filter(Boolean)
        .join(' ') || undefined,
    'aria-invalid': (invalid ?? Boolean(error)) || undefined,
  };

  // THREE CASES, IN THIS ORDER:
  //
  //   • a FUNCTION child owns its own wiring, so the label can always point at
  //     the id we handed it;
  //   • an explicit `htmlFor` means the CALLER has already wired the control —
  //     so nothing is injected. Injecting would put that id on the child too
  //     (a wrapper `<div>`, typically), and two elements sharing an id is not a
  //     label, it is a document that no longer resolves. Caught by a test that
  //     did exactly that;
  //   • otherwise a single element child is wired here, and anything else is
  //     left alone so the label makes no claim it cannot keep.
  const labelClass = `block font-mono ${small ? 'text-[10px]' : 'text-[11px]'} font-semibold uppercase tracking-wider text-ink-3 mb-1.5`;

  // A GROUP NAMES ITS OWN WRAPPER, so nothing is injected into the children:
  // each control inside keeps whatever name it already has (a button's text),
  // and the group adds the title on top. Hint and error describe the group.
  if (group) {
    const labelId = safeId('group-label', auto);
    return (
      <div role="group" aria-labelledby={labelId} aria-describedby={control['aria-describedby']}>
        <span id={labelId} className={labelClass}>
          {label}
        </span>
        {typeof children === 'function' ? children(control) : children}
        {hint ? (
          <p id={safeId('hint', auto)} className="mt-1 text-[11px] text-ink-3">
            {hint}
          </p>
        ) : null}
        {error ? (
          <StatusMessage id={safeId('error', auto)} kind="error" className="mt-1">
            {error}
          </StatusMessage>
        ) : null}
      </div>
    );
  }

  const custom = typeof children === 'function';
  const wired = custom
    ? { node: children(control), forId: control.id }
    : htmlFor !== undefined
      ? { node: children, forId: htmlFor }
      : wireControl(children, control);

  return (
    <div>
      <label htmlFor={wired.forId} className={labelClass}>
        {label}
      </label>
      {wired.node}
      {hint ? (
        <p id={safeId('hint', auto)} className="mt-1 text-[11px] text-ink-3">
          {hint}
        </p>
      ) : null}
      {error ? (
        <StatusMessage id={safeId('error', auto)} kind="error" className="mt-1">
          {error}
        </StatusMessage>
      ) : null}
    </div>
  );
}
